import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import RotateRightIcon from "@material-ui/icons/RotateRight";
import GetAppIcon from "@material-ui/icons/GetApp";
import CloseIcon from "@material-ui/icons/Close";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import FilterCenterFocusIcon from "@material-ui/icons/FilterCenterFocus";
import CheckIcon from "@material-ui/icons/Check";
import { toast } from "react-toastify";

import api from "../../services/api";

/**
 * Imagem do atendimento: miniatura + visualizador em tela cheia.
 *
 * O visualizador (proprio, no lugar do react-modal-image) tem:
 *  - zoom pela rodinha do mouse (no ponto do cursor), pelos botoes ou + / -
 *  - arrastar para mover quando ampliada; duplo clique amplia/volta
 *  - girar e baixar
 *  - "Copiar texto": le o texto da imagem (curriculo em foto, print...) e
 *    mostra num painel onde da para selecionar um trecho ou copiar tudo.
 */

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 8;
const limitar = z => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

const useStyles = makeStyles(theme => ({
	moldura: {
		width: 250,
		overflow: "hidden",
		borderRadius: 8,
		backgroundColor: "rgba(0, 0, 0, 0.05)",
		display: "block",
		lineHeight: 0,
		"& > img": {
			width: "100%",
			height: "auto",
			maxHeight: 340,
			objectFit: "cover",
			display: "block",
			cursor: "zoom-in",
			margin: 0,
			borderRadius: 8,
		},
	},
	tela: {
		backgroundColor: "rgba(10, 12, 14, 0.96)",
		display: "flex",
		flexDirection: "column",
		height: "100%",
		color: "#fff",
	},
	barra: {
		display: "flex",
		alignItems: "center",
		gap: 2,
		padding: "6px 10px",
		flexWrap: "wrap",
		"& .MuiIconButton-root": { color: "#fff" },
	},
	titulo: {
		fontSize: 15,
		fontWeight: 600,
		marginRight: "auto",
		paddingLeft: 6,
	},
	porcentagem: {
		color: "#fff",
		minWidth: 58,
		fontVariantNumeric: "tabular-nums",
		textTransform: "none",
	},
	botaoTexto: {
		color: "#fff",
		textTransform: "none",
		marginLeft: 6,
		borderColor: "rgba(255,255,255,0.4)",
		"&.ativo": { backgroundColor: "rgba(255,255,255,0.16)" },
	},
	corpo: {
		flex: 1,
		display: "flex",
		minHeight: 0,
		[theme.breakpoints.down("sm")]: { flexDirection: "column" },
	},
	palco: {
		flex: 1,
		position: "relative",
		overflow: "hidden",
		touchAction: "none",
		userSelect: "none",
	},
	imagem: {
		position: "absolute",
		top: "50%",
		left: "50%",
		maxWidth: "94%",
		maxHeight: "94%",
		transformOrigin: "center center",
		willChange: "transform",
		boxShadow: "0 8px 30px rgba(0,0,0,0.5)",
		pointerEvents: "none",
	},
	painel: {
		width: 380,
		maxWidth: "100%",
		backgroundColor: "#fff",
		color: "#1f2933",
		display: "flex",
		flexDirection: "column",
		borderLeft: "1px solid rgba(0,0,0,0.1)",
		[theme.breakpoints.down("sm")]: { width: "100%", height: "45%", borderLeft: "none" },
	},
	painelTopo: {
		display: "flex",
		alignItems: "center",
		padding: "12px 14px",
		borderBottom: "1px solid rgba(0,0,0,0.08)",
		fontWeight: 600,
		fontSize: 14,
		"& > span": { marginRight: "auto" },
	},
	painelTexto: {
		flex: 1,
		overflow: "auto",
		margin: 0,
		padding: "12px 14px",
		fontFamily: "inherit",
		fontSize: 13.5,
		lineHeight: 1.55,
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
		userSelect: "text",
		cursor: "text",
	},
	painelDica: {
		padding: "8px 14px 12px",
		fontSize: 12,
		color: "#6b7280",
	},
	carregando: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		gap: 12,
		color: "#6b7280",
		fontSize: 13,
		padding: 20,
		textAlign: "center",
	},
}));

const copiarParaAreaDeTransferencia = async texto => {
	try {
		await navigator.clipboard.writeText(texto);
		return true;
	} catch (e) {
		// navegador sem permissao de clipboard: copia pelo jeito antigo
		const area = document.createElement("textarea");
		area.value = texto;
		area.style.position = "fixed";
		area.style.opacity = "0";
		document.body.appendChild(area);
		area.select();
		let ok = false;
		try {
			ok = document.execCommand("copy");
		} catch (err) {
			ok = false;
		}
		document.body.removeChild(area);
		return ok;
	}
};

const ModalImageCors = ({ imageUrl }) => {
	const classes = useStyles();
	const [blobUrl, setBlobUrl] = useState("");
	const [aberta, setAberta] = useState(false);

	const [zoom, setZoom] = useState(1);
	const [pos, setPos] = useState({ x: 0, y: 0 });
	const [giro, setGiro] = useState(0);
	const arrasto = useRef(null);
	const [arrastando, setArrastando] = useState(false);
	const palcoRef = useRef(null);
	// espelho do zoom/posicao para os calculos (sem efeito dentro do setState)
	const vista = useRef({ zoom: 1, pos: { x: 0, y: 0 } });
	useEffect(() => {
		vista.current = { zoom, pos };
	}, [zoom, pos]);

	const [painelAberto, setPainelAberto] = useState(false);
	const [texto, setTexto] = useState("");
	const [lendo, setLendo] = useState(false);
	const [erroTexto, setErroTexto] = useState("");
	const [copiado, setCopiado] = useState(false);

	useEffect(() => {
		if (!imageUrl) return undefined;
		let url = "";
		let ativo = true;
		const buscar = async () => {
			try {
				const { data, headers } = await api.get(imageUrl, { responseType: "blob" });
				url = window.URL.createObjectURL(new Blob([data], { type: headers["content-type"] }));
				if (ativo) setBlobUrl(url);
			} catch (e) {
				/* fica com a URL direta */
			}
		};
		buscar();
		return () => {
			ativo = false;
			if (url) window.URL.revokeObjectURL(url);
		};
	}, [imageUrl]);

	const src = blobUrl || imageUrl;

	const ajustar = useCallback(() => {
		setZoom(1);
		setPos({ x: 0, y: 0 });
	}, []);

	const abrir = () => {
		ajustar();
		setGiro(0);
		setAberta(true);
	};

	const fechar = () => {
		setAberta(false);
		setPainelAberto(false);
	};

	// Zoom mantendo parado o ponto que esta sob o cursor (ou o centro).
	const zoomPara = useCallback((novo, clienteX, clienteY) => {
		const palco = palcoRef.current;
		const { zoom: atual, pos: p } = vista.current;
		const alvo = limitar(novo(atual));
		let nova = { x: p.x * (alvo / atual), y: p.y * (alvo / atual) };
		if (palco && clienteX !== undefined) {
			const r = palco.getBoundingClientRect();
			const px = clienteX - r.left - r.width / 2;
			const py = clienteY - r.top - r.height / 2;
			nova = { x: px - (px - p.x) * (alvo / atual), y: py - (py - p.y) * (alvo / atual) };
		}
		if (alvo <= 1) nova = { x: 0, y: 0 };
		vista.current = { zoom: alvo, pos: nova };
		setZoom(alvo);
		setPos(nova);
	}, []);

	// Rodinha do mouse (listener nativo para poder usar preventDefault).
	useEffect(() => {
		const palco = palcoRef.current;
		if (!aberta || !palco) return undefined;
		const naRodinha = e => {
			e.preventDefault();
			const fator = e.deltaY < 0 ? 1.15 : 1 / 1.15;
			zoomPara(z => z * fator, e.clientX, e.clientY);
		};
		palco.addEventListener("wheel", naRodinha, { passive: false });
		return () => palco.removeEventListener("wheel", naRodinha);
	}, [aberta, painelAberto, zoomPara]);

	// Atalhos: + / - / 0
	useEffect(() => {
		if (!aberta) return undefined;
		const naTecla = e => {
			if (e.target && ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
			if (e.key === "+" || e.key === "=") zoomPara(z => z * 1.25);
			else if (e.key === "-") zoomPara(z => z / 1.25);
			else if (e.key === "0") ajustar();
		};
		window.addEventListener("keydown", naTecla);
		return () => window.removeEventListener("keydown", naTecla);
	}, [aberta, zoomPara, ajustar]);

	const aoApertar = e => {
		if (e.button !== undefined && e.button !== 0) return;
		e.currentTarget.setPointerCapture(e.pointerId);
		arrasto.current = { x: e.clientX, y: e.clientY, inicio: pos };
		setArrastando(true);
	};
	const aoMover = e => {
		const a = arrasto.current;
		if (!a) return;
		setPos({ x: a.inicio.x + (e.clientX - a.x), y: a.inicio.y + (e.clientY - a.y) });
	};
	const aoSoltar = () => {
		arrasto.current = null;
		setArrastando(false);
	};
	const duploClique = e => {
		if (zoom > 1.01) ajustar();
		else zoomPara(() => 2.5, e.clientX, e.clientY);
	};

	const baixar = () => {
		const a = document.createElement("a");
		a.href = src;
		a.download = decodeURIComponent(String(imageUrl || "imagem.jpg").split("?")[0].split("/").pop()) || "imagem.jpg";
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	};

	const lerTexto = async () => {
		setPainelAberto(true);
		if (texto || lendo) return;
		setLendo(true);
		setErroTexto("");
		try {
			const { data } = await api.post("/ai/texto-da-imagem", { mediaUrl: imageUrl });
			setTexto(String(data?.texto || ""));
		} catch (err) {
			setErroTexto(err?.response?.data?.error || err?.response?.data?.message || "Não consegui ler o texto da imagem.");
		} finally {
			setLendo(false);
		}
	};

	const copiarTudo = async () => {
		if (!texto) return;
		const ok = await copiarParaAreaDeTransferencia(texto);
		if (ok) {
			setCopiado(true);
			setTimeout(() => setCopiado(false), 1800);
		} else {
			toast.error("Não deu para copiar. Selecione o texto e use Ctrl+C.");
		}
	};

	return (
		<>
			<div className={classes.moldura}>
				<img src={src} alt="imagem" onClick={abrir} />
			</div>

			<Dialog fullScreen open={aberta} onClose={fechar} PaperProps={{ style: { backgroundColor: "transparent" } }}>
				<div className={classes.tela}>
					<div className={classes.barra}>
						<span className={classes.titulo}>Imagem</span>

						<Tooltip title="Diminuir (−)">
							<IconButton size="small" onClick={() => zoomPara(z => z / 1.25)}>
								<ZoomOutIcon />
							</IconButton>
						</Tooltip>
						<Tooltip title="Voltar ao tamanho da tela (0)">
							<Button size="small" className={classes.porcentagem} onClick={ajustar}>
								{Math.round(zoom * 100)}%
							</Button>
						</Tooltip>
						<Tooltip title="Ampliar (+)">
							<IconButton size="small" onClick={() => zoomPara(z => z * 1.25)}>
								<ZoomInIcon />
							</IconButton>
						</Tooltip>
						<Tooltip title="Ajustar à tela">
							<IconButton size="small" onClick={ajustar}>
								<FilterCenterFocusIcon />
							</IconButton>
						</Tooltip>
						<Tooltip title="Girar">
							<IconButton size="small" onClick={() => setGiro(g => (g + 90) % 360)}>
								<RotateRightIcon />
							</IconButton>
						</Tooltip>
						<Tooltip title="Baixar">
							<IconButton size="small" onClick={baixar}>
								<GetAppIcon />
							</IconButton>
						</Tooltip>

						<Button
							size="small"
							variant="outlined"
							startIcon={<TextFieldsIcon />}
							className={`${classes.botaoTexto} ${painelAberto ? "ativo" : ""}`}
							onClick={() => (painelAberto ? setPainelAberto(false) : lerTexto())}
						>
							Copiar texto
						</Button>

						<Tooltip title="Fechar (Esc)">
							<IconButton size="small" onClick={fechar} style={{ marginLeft: 6 }}>
								<CloseIcon />
							</IconButton>
						</Tooltip>
					</div>

					<div className={classes.corpo}>
						<div
							ref={palcoRef}
							className={classes.palco}
							style={{ cursor: zoom > 1 ? (arrastando ? "grabbing" : "grab") : "zoom-in" }}
							onPointerDown={aoApertar}
							onPointerMove={aoMover}
							onPointerUp={aoSoltar}
							onPointerCancel={aoSoltar}
							onDoubleClick={duploClique}
						>
							<img
								src={src}
								alt="imagem"
								draggable={false}
								className={classes.imagem}
								style={{
									transform: `translate(-50%, -50%) translate(${pos.x}px, ${pos.y}px) scale(${zoom}) rotate(${giro}deg)`,
									transition: arrastando ? "none" : "transform 0.12s ease-out",
								}}
							/>
						</div>

						{painelAberto && (
							<div className={classes.painel}>
								<div className={classes.painelTopo}>
									<span>Texto da imagem</span>
									{texto && (
										<Button
											size="small"
											color="primary"
											variant="contained"
											disableElevation
											startIcon={copiado ? <CheckIcon /> : <FileCopyOutlinedIcon />}
											onClick={copiarTudo}
										>
											{copiado ? "Copiado!" : "Copiar tudo"}
										</Button>
									)}
									<IconButton size="small" onClick={() => setPainelAberto(false)} style={{ marginLeft: 4 }}>
										<CloseIcon fontSize="small" />
									</IconButton>
								</div>

								{lendo && (
									<div className={classes.carregando}>
										<CircularProgress size={28} />
										Lendo o texto da imagem…
									</div>
								)}
								{!lendo && erroTexto && (
									<div className={classes.carregando}>
										{erroTexto}
										<Button
											size="small"
											variant="outlined"
											onClick={() => {
												setErroTexto("");
												setTexto("");
												lerTexto();
											}}
										>
											Tentar de novo
										</Button>
									</div>
								)}
								{!lendo && !erroTexto && texto && (
									<>
										<pre className={classes.painelTexto}>{texto}</pre>
										<div className={classes.painelDica}>
											Selecione um trecho com o mouse e use Ctrl+C, ou copie tudo. O texto é lido por IA: confira números
											importantes (telefone, e-mail) antes de usar.
										</div>
									</>
								)}
							</div>
						)}
					</div>
				</div>
			</Dialog>
		</>
	);
};

export default ModalImageCors;
